import { useState } from "react"
import { useDispatch } from "react-redux"
import { createResult, fetchResults } from "../store/slices/resultSlice"
import { BookOpen, X } from "lucide-react"

const AddResult = ({ setShowAddModal }) => {
  const dispatch = useDispatch()
  const [formLoading, setFormLoading] = useState(false)
  const [formData, setFormData] = useState({
    student_id: "",
    exam_name: "",
    marks: "",
    status: "",
  })
  const [message, setMessage] = useState({ text: "", type: "" })

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setFormLoading(true)

    // Basic form validation
    if (!formData.student_id || isNaN(formData.student_id) || Number(formData.student_id) <= 0) {
      setMessage({ text: "Please enter a valid Student ID", type: "error" })
      setFormLoading(false)
      return
    }
    if (!formData.exam_name) {
      setMessage({ text: "Please enter an exam name", type: "error" })
      setFormLoading(false)
      return
    }
    if (formData.marks && (isNaN(formData.marks) || formData.marks < 0 || formData.marks > 100)) {
      setMessage({ text: "Marks must be between 0 and 100", type: "error" })
      setFormLoading(false)
      return
    }
    if (!formData.status) {
      setMessage({ text: "Please select a status", type: "error" })
      setFormLoading(false)
      return
    }

    dispatch(createResult(formData))
      .unwrap()
      .then(() => {
        setMessage({ text: "Result created successfully!", type: "success" })
        dispatch(fetchResults())
        setFormData({
          student_id: "",
          exam_name: "",
          marks: "",
          status: "",
        })
        setShowAddModal(false)
      })
      .catch((err) => {
        setMessage({
          text: `Error creating result: ${err.message || err}`,
          type: "error",
        })
      })
      .finally(() => setFormLoading(false))
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <BookOpen className="w-6 h-6 text-blue-600" />
          <h3 className="text-xl font-semibold text-foreground">Add New Result</h3>
        </div>
        <button
          onClick={() => setShowAddModal(false)}
          className="p-2 text-gray-600 hover:text-gray-800"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {message.text && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            message.type === "success" ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"
          }`}
        >
          <p className={message.type === "success" ? "text-green-700" : "text-red-700"}>{message.text}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Student ID:</label>
            <input
              type="number"
              name="student_id"
              value={formData.student_id}
              onChange={handleInputChange}
              required
              min="1"
              disabled={formLoading}
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Exam Name:</label>
            <input
              type="text"
              name="exam_name"
              value={formData.exam_name}
              onChange={handleInputChange}
              required
              disabled={formLoading}
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Marks:</label>
            <input
              type="number"
              name="marks"
              value={formData.marks}
              onChange={handleInputChange}
              min="0"
              max="100"
              disabled={formLoading}
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Status:</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              required
              disabled={formLoading}
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Status</option>
              <option value="Pass">Pass</option>
              <option value="Fail">Fail</option>
            </select>
          </div>
        </div>
        <div className="mt-6 flex space-x-4">
          <button
            type="submit"
            className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
            disabled={formLoading}
          >
            Create Result
          </button>
          <button
            type="button"
            onClick={() => setShowAddModal(false)}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-lg transition duration-200"
            disabled={formLoading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}

export default AddResult